// Renders a PDF as a vertical stack of pages (or rows of two, in the book
// layout) directly in normal document flow
// (not an inner scrollable div), so the reader's existing window-scroll based
// progress tracking (currentRatio/persistProgress/restoreScroll in reader.ts)
// keeps working completely unchanged — this view never needs its own scroll or
// position handling.
//
// Each page is a canvas (the picture) under a transparent text layer (real DOM
// text, positioned over the glyphs). The text layer is what makes the PDF
// selectable and copyable, and it is also the surface highlights are painted
// into: to the annotation code a page's text layer is just an article, so
// shared/highlight.ts and shared/anchor.ts work on it unchanged. Pages render
// lazily, so highlights are painted per page as each text layer appears
// (`hooks.onTextLayer`).
import { api, TOKEN_KEY } from './store.ts';

// pdf.js needs its worker as a separately-loadable script; build.js copies
// it into dist/ next to the app bundle (see buildWeb() there).
const WORKER_SRC = 'dist/pdf.worker.min.mjs';

type PdfjsLib = Awaited<ReturnType<typeof importPdfjs>>;
type PdfLoadingTask = ReturnType<PdfjsLib['getDocument']>;
type PdfDocument = Awaited<PdfLoadingTask['promise']>;
type PdfPage = Awaited<ReturnType<PdfDocument['getPage']>>;
type RenderTask = ReturnType<PdfPage['render']>;

let pdfjsPromise: Promise<PdfjsLib> | null = null;

function importPdfjs(): Promise<typeof import('pdfjs-dist')> {
  // Bare specifier rather than the 'build/pdf.mjs' subpath: it resolves to
  // the exact same file (package.json's "main"), but only the bare import
  // also picks up type declarations — the subpath has none of its own.
  return import('pdfjs-dist').then((lib) => {
    lib.GlobalWorkerOptions.workerSrc = WORKER_SRC;
    return lib;
  });
}

function loadPdfjs(): Promise<PdfjsLib> {
  if (!pdfjsPromise) pdfjsPromise = importPdfjs();
  return pdfjsPromise;
}

/** Start fetching pdf.js ahead of openPdf, so it downloads while the caller
 *  is still busy with other requests. */
export function preloadPdfViewer(): void {
  void loadPdfjs().catch(() => {
    pdfjsPromise = null; // let openPdf try again and report the failure
  });
}

export const ZOOM_LEVELS = [0.75, 1, 1.25, 1.5, 2, 3];
const ZOOM_KEY = 'altfresh_pdf_zoom';

/** 'book' shows pages two to a row when they fit; 'book-cover' also sets the
 *  first page alone on the right, so spreads pair 2–3, 4–5 like a printed book. */
export type PdfLayout = 'single' | 'book' | 'book-cover';
const LAYOUTS: PdfLayout[] = ['single', 'book', 'book-cover'];
const LAYOUT_KEY = 'altfresh_pdf_layout';

/** 'theme' tints pages to the reader theme (style.css); 'original' leaves them as printed. */
export type PdfColors = 'theme' | 'original';
const COLORS: PdfColors[] = ['theme', 'original'];
const COLORS_KEY = 'altfresh_pdf_colors';

// Two pages go side by side only if each can be at least this wide; narrower,
// a page's text is too small to read, and one column is the better use of it.
const MIN_BOOK_PAGE = 360;
// The gap between the two pages of a spread — the spine (style.css matches it).
const SPINE = 4;

/** 'scroll' is one long column of pages; 'paged' shows one page (or spread)
 *  at a time and turns to the next. */
export type PdfReading = 'scroll' | 'paged';
const READINGS: PdfReading[] = ['scroll', 'paged'];
const READING_KEY = 'altfresh_pdf_reading';

const TURN_MS = 500;

// Page sizes are read this many at a time, in the background after the viewer
// is already showing. Reading them one after another (a round trip to the
// worker each) is what made a long PDF slow to open.
const SIZE_BATCH = 16;

// Pages render when they come within RENDER_MARGIN of the viewport and give
// their canvas back once they are more than RELEASE_MARGIN away. Without the
// release a long document keeps every page it has ever shown: a retina A4 page
// is ~17MB of canvas, so a hundred pages is enough to crash the tab.
const RENDER_MARGIN = '800px 0px';
const RELEASE_MARGIN = '3500px 0px';

// A canvas is re-rendered only when the size it's shown at drifts this far
// from the size it was drawn at — enough to stay sharp after a zoom, not so
// tight that dragging the window edge redraws every page continuously.
const RERENDER_TOLERANCE = 0.3;

// Browsers refuse or silently blank canvases past a few tens of millions of
// pixels; a high zoom on a large page would otherwise ask for one.
const MAX_CANVAS_PIXELS = 16_000_000;

/** Where the reader line sits, as a fraction of the window height: the page
 *  under it is "the current page", and zooming keeps it fixed in place. */
const READING_LINE = 0.35;

export interface PdfHooks {
  /** A page's text layer is in the DOM — paint its highlights. */
  onTextLayer?: (pageNumber: number, layer: HTMLElement) => void;
  /** Every page's size has been read and the typical page shape turned out
   *  different from `PdfInfo.pageAspect`, which is only page 1's. */
  onPageAspect?: (aspect: number) => void;
}

export interface PdfOutlineItem {
  title: string;
  /** 1-based page the entry points at. */
  page: number;
  depth: number;
}

export interface PdfInfo {
  pageCount: number;
  /** Width / height of a typical page — the book layout sizes its column so a
   *  spread of two fits the window's height. Page 1's at first; the
   *  `onPageAspect` hook reports the real one once every page has been read. */
  pageAspect: number;
  /** Resolved on demand — a long outline can take a moment, and most readers
   *  never open it. */
  loadOutline: () => Promise<PdfOutlineItem[]>;
}

interface PageState {
  number: number;
  wrapper: HTMLElement;
  /** Page size in PDF units (1 unit = 1/72in), after the page's own rotation. */
  widthUnits: number;
  heightUnits: number;
  canvas: HTMLCanvasElement | null;
  /** The scale of the canvas drawn or being drawn; 0 when there is none. */
  targetScale: number;
  task: RenderTask | null;
  /** Bumped on every (re)render and release, so a render that was superseded
   *  can tell and discard its result. */
  generation: number;
  hasTextLayer: boolean;
}

interface Viewer {
  lib: PdfjsLib;
  doc: PdfDocument;
  loadingTask: PdfLoadingTask;
  container: HTMLElement;
  pages: PageState[];
  hooks: PdfHooks;
  renderObserver: IntersectionObserver;
  releaseObserver: IntersectionObserver;
  resizeObserver: ResizeObserver;
  scrollHandler: () => void;
  /** Removes the container's own listeners (swipes), which outlive the document. */
  listeners: AbortController;
  currentPage: number;
}

let viewer: Viewer | null = null;
let layout: PdfLayout = readSetting(LAYOUT_KEY, LAYOUTS, 'single');
let colors: PdfColors = readSetting(COLORS_KEY, COLORS, 'theme');
let reading: PdfReading = readSetting(READING_KEY, READINGS, 'paged');
let pageListener: ((page: number) => void) | null = null;

/** Called with the page number whenever the page under the reading line changes. */
export function onPdfPageChange(fn: (page: number) => void): void {
  pageListener = fn;
}

export function currentPdfPage(): number {
  return viewer?.currentPage ?? 1;
}

function readSetting<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const saved = localStorage.getItem(key) as T | null;
    if (saved && allowed.includes(saved)) return saved;
  } catch {
    // Storage can be blocked; the default is fine.
  }
  return fallback;
}

function saveSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not worth surfacing.
  }
}

function readZoom(): number {
  try {
    const saved = Number(localStorage.getItem(ZOOM_KEY));
    if (ZOOM_LEVELS.includes(saved)) return saved;
  } catch {
    // Storage can be blocked; the default is fine.
  }
  return 1;
}

export function getPdfZoom(): number {
  return viewer ? Number(viewer.container.style.getPropertyValue('--pdf-zoom')) || 1 : readZoom();
}

/** How many canvas pixels per PDF unit this page should be drawn at right now. */
function canvasScale(page: PageState): number {
  const dpr = window.devicePixelRatio || 1;
  const cssPerUnit = page.wrapper.clientWidth / page.widthUnits;
  const wanted = (cssPerUnit || 1.5) * dpr;
  const limit = Math.sqrt(MAX_CANVAS_PIXELS / (page.widthUnits * page.heightUnits));
  return Math.max(0.5, Math.min(wanted, limit));
}

/** Tell the text layer how many CSS pixels one PDF unit currently is — its
 *  glyph sizes are all expressed against this one variable. */
function applyScaleFactor(page: PageState): void {
  const factor = page.wrapper.clientWidth / page.widthUnits;
  if (factor > 0) page.wrapper.style.setProperty('--total-scale-factor', String(factor));
}

function isNearViewport(wrapper: HTMLElement, margin = 800): boolean {
  const rect = wrapper.getBoundingClientRect();
  return rect.bottom > -margin && rect.top < window.innerHeight + margin;
}

// ── Text layer ────────────────────────────────────────────────────────────────

/**
 * A run ending its line is followed by a <br>, which contributes no text — so
 * flattening the layer would glue the last word of one line to the first of
 * the next ("end.Next"), and every quote spanning a line break would be
 * stored that way. Give each such run a trailing space. A trailing hyphen is
 * left alone: "exam-" + "ple" reads better as "exam-ple" than "exam- ple".
 */
function spaceLineEnds(layer: HTMLElement): void {
  for (const br of layer.querySelectorAll('br')) {
    const run = br.previousElementSibling;
    const text = run?.textContent;
    if (run && text && !/\s$/.test(text) && !/[A-Za-z]-$/.test(text)) run.append(' ');
  }
}

// Chrome's selection jumps to the end of the page when a drag crosses the gap
// between two absolutely-positioned runs; pdf.js's fix is a tall invisible
// element that a mousedown on the layer stretches over the whole layer (see
// `.selecting` in style.css). It has to be cleared again wherever the mouse is
// released, not just over the layer.
document.addEventListener('mouseup', () => {
  for (const layer of document.querySelectorAll('.pdf-text-layer.selecting')) {
    layer.classList.remove('selecting');
  }
});

async function buildTextLayer(v: Viewer, state: PageState, page: PdfPage): Promise<void> {
  if (state.hasTextLayer) return;
  // A rotated page's text layer needs the rotation applied to it as well, which
  // isn't worth the code for how rare these are; its canvas still renders.
  if (page.rotate % 360 !== 0) return;
  state.hasTextLayer = true;

  const layer = document.createElement('div');
  layer.className = 'textLayer pdf-text-layer';
  layer.dataset.pageNumber = String(state.number);
  layer.addEventListener('mousedown', () => layer.classList.add('selecting'));
  state.wrapper.append(layer);

  try {
    await new v.lib.TextLayer({
      textContentSource: page.streamTextContent(),
      container: layer,
      viewport: page.getViewport({ scale: 1 }),
    }).render();
  } catch (err) {
    layer.remove();
    state.hasTextLayer = false;
    console.error(`Text layer for page ${state.number} failed`, err);
    return;
  }
  if (v !== viewer) return;

  spaceLineEnds(layer);
  const end = document.createElement('div');
  end.className = 'endOfContent';
  layer.append(end);
  v.hooks.onTextLayer?.(state.number, layer);
}

// ── Page rendering ────────────────────────────────────────────────────────────

function releasePage(state: PageState): void {
  state.generation++;
  state.task?.cancel();
  state.task = null;
  state.targetScale = 0;
  if (state.canvas) {
    // Zeroing the size is what actually returns the pixel buffer; removing the
    // element alone leaves it for the garbage collector to get to eventually.
    state.canvas.width = 0;
    state.canvas.height = 0;
    state.canvas.remove();
    state.canvas = null;
  }
}

async function drawPage(v: Viewer, state: PageState, scale: number, generation: number): Promise<void> {
  try {
    const page = await v.doc.getPage(state.number);
    if (v !== viewer || generation !== state.generation) return;
    // The placeholder may still be using page 1's size (see readPageSizes).
    resizePages(v, [[state, page.getViewport({ scale: 1 })]]);
    if (generation !== state.generation) return; // the resize re-rendered it

    // Not awaited: the text is usable before the picture finishes drawing.
    void buildTextLayer(v, state, page);

    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    // Display size comes from CSS (width: 100%; height: auto in style.css),
    // not an inline style — canvas.width/height are the backing-buffer
    // resolution, and an inline style would win over the stylesheet's
    // height:auto and distort the page whenever the container is narrower.

    const task = page.render({ canvas, viewport });
    state.task = task;
    await task.promise;
    if (v !== viewer || generation !== state.generation) return;

    state.canvas?.remove();
    state.wrapper.prepend(canvas);
    state.canvas = canvas;
  } catch (err) {
    if (err instanceof v.lib.RenderingCancelledException) return;
    console.error(`Rendering page ${state.number} failed`, err);
    // Allow the next intersection to try again.
    if (generation === state.generation) state.targetScale = 0;
  } finally {
    if (generation === state.generation) state.task = null;
  }
}

/** Draw a page, unless what is already drawn (or being drawn) is sharp enough. */
function renderPage(v: Viewer, state: PageState): void {
  const scale = canvasScale(state);
  if (state.targetScale && Math.abs(scale / state.targetScale - 1) <= RERENDER_TOLERANCE) return;

  state.targetScale = scale;
  state.task?.cancel();
  void drawPage(v, state, scale, ++state.generation);
}

/** After the shown size changes: redraw a visible page, drop a distant one. */
function refreshPage(v: Viewer, state: PageState): void {
  // A page the paged view isn't showing has no size to draw it at.
  if (!state.wrapper.clientWidth) return;
  applyScaleFactor(state);
  if (!state.targetScale) return;
  const scale = canvasScale(state);
  if (Math.abs(scale / state.targetScale - 1) <= RERENDER_TOLERANCE) return;
  if (isNearViewport(state.wrapper)) renderPage(v, state);
  else releasePage(state);
}

// ── Current page and navigation ───────────────────────────────────────────────

function updateCurrentPage(v: Viewer): void {
  const line = window.innerHeight * READING_LINE;
  let n = v.currentPage;
  const rect = (i: number): DOMRect => v.pages[i - 1]!.wrapper.getBoundingClientRect();

  // Walk from the last known page: scrolling moves it by a page or two, so
  // this is a handful of rect reads however long the document is.
  while (n > 1 && rect(n).top > line) n--;
  while (n < v.pages.length && rect(n).bottom < line) n++;
  // Side by side, both pages of a row are under the line: call it the left one.
  if (v.container.hasAttribute('data-spread')) {
    while (n > 1 && rect(n - 1).top === rect(n).top) n--;
  }

  if (n !== v.currentPage) {
    v.currentPage = n;
    pageListener?.(n);
  }
}

export function scrollToPdfPage(page: number, behavior: ScrollBehavior = 'auto'): void {
  if (!viewer) return;
  const n = Math.min(Math.max(1, Math.round(page)), viewer.pages.length);
  if (isPaged(viewer)) {
    finishFlip();
    showSpread(viewer, n);
    alignSpread(viewer);
    return;
  }
  viewer.pages[n - 1]!.wrapper.scrollIntoView({ block: 'start', behavior });
}

// ── Zoom ──────────────────────────────────────────────────────────────────────

/**
 * Make a change that reflows the pages, holding the point under the reading
 * line fixed: the same spot on the same page, however much the document moves
 * around it. Then redraw pages whose size changed.
 */
function keepReadingPoint(v: Viewer, change: () => void, changed: PageState[] = v.pages): void {
  if (isPaged(v)) {
    // Nothing scrolls away from under the reader here — just re-pair the
    // pages, since a layout change can move where the spreads split.
    finishFlip();
    change();
    showSpread(v, v.currentPage);
    for (const state of changed) refreshPage(v, state);
    return;
  }
  const line = window.innerHeight * READING_LINE;
  const before = v.pages[v.currentPage - 1]!.wrapper.getBoundingClientRect();
  const fraction = (line - before.top) / before.height;

  change();

  const after = v.pages[v.currentPage - 1]!.wrapper.getBoundingClientRect(); // forces layout
  window.scrollBy(0, after.top + fraction * after.height - line);

  for (const state of changed) refreshPage(v, state);
}

export function setPdfZoom(zoom: number): number {
  if (!viewer) return zoom;
  keepReadingPoint(viewer, () => {
    viewer!.container.style.setProperty('--pdf-zoom', String(zoom));
    viewer!.container.toggleAttribute('data-zoomed', zoom > 1);
  });
  saveSetting(ZOOM_KEY, String(zoom));
  return zoom;
}

/** Move one step through ZOOM_LEVELS; returns the resulting zoom. */
export function stepPdfZoom(direction: 1 | -1): number {
  const current = getPdfZoom();
  // The nearest defined level, so a stored or externally set value can't strand the buttons.
  let index = ZOOM_LEVELS.reduce(
    (best, level, i) => (Math.abs(level - current) < Math.abs(ZOOM_LEVELS[best]! - current) ? i : best), 0,
  );
  index = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, index + direction));
  return setPdfZoom(ZOOM_LEVELS[index]!);
}

export function resetPdfZoom(): number {
  return setPdfZoom(1);
}

// ── Layout and colors ─────────────────────────────────────────────────────────

export function getPdfLayout(): PdfLayout {
  return layout;
}

export function getPdfColors(): PdfColors {
  return colors;
}

function applyLayout(container: HTMLElement): void {
  container.dataset.layout = layout;
  container.toggleAttribute('data-cover', layout === 'book-cover');
}

/** Whether pages should sit side by side right now: book layout, and room for two. */
function wantsSpread(container: HTMLElement): boolean {
  return layout !== 'single' && container.clientWidth >= 2 * MIN_BOOK_PAGE + SPINE;
}

/** Re-decide one column vs two after the column's width changed. */
function updateSpread(v: Viewer): void {
  const spread = wantsSpread(v.container);
  if (spread === v.container.hasAttribute('data-spread')) return;
  keepReadingPoint(v, () => v.container.toggleAttribute('data-spread', spread));
}

export function setPdfLayout(next: PdfLayout): void {
  layout = next;
  saveSetting(LAYOUT_KEY, next);
  if (!viewer) return;
  const v = viewer;
  keepReadingPoint(v, () => {
    applyLayout(v.container);
    // The book layout also widens the column (style.css), so this reads the new width.
    v.container.toggleAttribute('data-spread', wantsSpread(v.container));
  });
}

export function setPdfColors(next: PdfColors): void {
  colors = next;
  saveSetting(COLORS_KEY, next);
  if (viewer) viewer.container.dataset.colors = next;
}

// ── Paged reading ─────────────────────────────────────────────────────────────
//
// The same page elements as the scroll view, with all but the current spread
// hidden. The spreads either side are laid out invisibly in the same spot
// (`.pdf-near`), so the render observers draw them ahead of a turn and the
// flip has real pictures to animate. Position is still one number: the
// spread's place in the document, as a 0–1 ratio (pdfPagedRatio).

let flip: { animation: Animation; overlay: HTMLElement; container: HTMLElement } | null = null;

function isPaged(v: Viewer): boolean {
  return v.container.hasAttribute('data-paged');
}

export function isPdfPaged(): boolean {
  return !!viewer && isPaged(viewer);
}

export function getPdfReading(): PdfReading {
  return reading;
}

/** First and last page of the spread that page `n` belongs to. */
function spreadOf(v: Viewer, n: number): [number, number] {
  if (!v.container.hasAttribute('data-spread')) return [n, n];
  const cover = layout === 'book-cover';
  // Book: 1–2, 3–4, …  Cover alone: 1, 2–3, 4–5, …
  const first = cover ? (n === 1 ? 1 : n - (n % 2)) : n - ((n + 1) % 2);
  const last = cover && first === 1 ? 1 : Math.min(v.pages.length, first + 1);
  return [first, last];
}

function showSpread(v: Viewer, n: number): void {
  const [first, last] = spreadOf(v, n);
  const prev = first > 1 ? spreadOf(v, first - 1) : null;
  const next = last < v.pages.length ? spreadOf(v, last + 1) : null;
  const within = (k: number, range: [number, number] | null): boolean => !!range && k >= range[0] && k <= range[1];

  for (const state of v.pages) {
    const k = state.number;
    const shown = k >= first && k <= last;
    state.wrapper.classList.toggle('pdf-shown', shown);
    state.wrapper.classList.toggle('pdf-near', !shown && (within(k, prev) || within(k, next)));
    state.wrapper.removeAttribute('data-side');
  }
  // A page alone in a spread keeps to its side of the spine: the cover on the
  // right, an odd last page on the left.
  if (first === last && v.container.hasAttribute('data-spread')) {
    v.pages[first - 1]!.wrapper.dataset.side = first === 1 && layout === 'book-cover' ? 'right' : 'left';
  }

  if (first !== v.currentPage) {
    v.currentPage = first;
    pageListener?.(first);
  }
}

/** Bring the spread's top edge to the top of the window, under the toolbar. */
function alignSpread(v: Viewer): void {
  v.pages[v.currentPage - 1]!.wrapper.scrollIntoView({ block: 'start' });
}

function clearSpread(v: Viewer): void {
  for (const state of v.pages) {
    state.wrapper.classList.remove('pdf-shown', 'pdf-near');
    state.wrapper.removeAttribute('data-side');
  }
}

/** Where the current spread is in the document, 0 (first) to 1 (last) —
 *  what the reader saves as the position while paging. */
export function pdfPagedRatio(): number {
  if (!viewer) return 0;
  const lastFirst = spreadOf(viewer, viewer.pages.length)[0];
  if (lastFirst <= 1) return 1;
  return (spreadOf(viewer, viewer.currentPage)[0] - 1) / (lastFirst - 1);
}

export function showPdfRatio(ratio: number): void {
  if (!viewer) return;
  const lastFirst = spreadOf(viewer, viewer.pages.length)[0];
  scrollToPdfPage(1 + Math.round(Math.min(1, Math.max(0, ratio)) * (lastFirst - 1)));
}

export function setPdfReading(next: PdfReading): void {
  reading = next;
  saveSetting(READING_KEY, next);
  if (!viewer) return;
  const v = viewer;
  finishFlip();
  const page = v.currentPage;
  v.container.toggleAttribute('data-paged', next === 'paged');
  if (next === 'paged') {
    showSpread(v, page);
    alignSpread(v);
  } else {
    clearSpread(v);
    v.pages[page - 1]!.wrapper.scrollIntoView({ block: 'start' });
  }
  for (const state of v.pages) refreshPage(v, state);
}

/** Turn to the next (1) or previous (-1) page or spread. */
export function turnPdfPage(direction: 1 | -1): void {
  const v = viewer;
  if (!v || !isPaged(v)) return;
  finishFlip();

  const [first, last] = spreadOf(v, v.currentPage);
  const target = direction > 0 ? last + 1 : first - 1;
  if (target < 1 || target > v.pages.length) return;

  const origin = v.container.getBoundingClientRect();
  const snapshot = (): Leaf[] => v.pages
    .filter((state) => state.wrapper.classList.contains('pdf-shown'))
    .map((state) => {
      const rect = state.wrapper.getBoundingClientRect();
      return {
        state, side: state.wrapper.dataset.side,
        left: rect.left - origin.left, top: rect.top - origin.top, width: rect.width, height: rect.height,
      };
    });

  const from = snapshot();
  showSpread(v, target);
  const to = snapshot();
  alignSpread(v);

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    animateTurn(v, from, to, direction);
  }
}

/** A shown page as it was laid out — captured before and after the turn, since
 *  showSpread rewrites the classes and sides the animation needs to know. */
interface Leaf {
  state: PageState;
  side: string | undefined;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A still of a page, placed where it was (or will be) shown. */
function pageFace(leaf: Leaf | null, className = 'pdf-flip-face'): HTMLElement {
  const face = document.createElement('div');
  face.className = className;
  const source = leaf?.state.canvas;
  if (source) {
    const copy = document.createElement('canvas');
    copy.width = source.width;
    copy.height = source.height;
    copy.getContext('2d')?.drawImage(source, 0, 0);
    face.append(copy);
  }
  return face;
}

function place(el: HTMLElement, leaf: Leaf): HTMLElement {
  Object.assign(el.style, {
    left: `${leaf.left}px`, top: `${leaf.top}px`, width: `${leaf.width}px`, height: `${leaf.height}px`,
  });
  return el;
}

/**
 * The turn itself, drawn entirely from stills over the real pages (hidden
 * meanwhile), so selection, highlights and rendering never see a half-turned
 * page. In a spread the page turns on the spine: its front is the page being
 * left, its back the page it lands as. A single page lifts away to the left
 * going forward, and comes back from there going back.
 */
function animateTurn(v: Viewer, from: Leaf[], to: Leaf[], direction: 1 | -1): void {
  const overlay = document.createElement('div');
  overlay.className = 'pdf-flip';
  const leaf = document.createElement('div');
  leaf.className = 'pdf-flip-leaf';

  let keyframes: Keyframe[];
  if (v.container.hasAttribute('data-spread')) {
    const side = (leaves: Leaf[], which: 'left' | 'right'): Leaf | null => {
      if (leaves.length === 2) return which === 'left' ? leaves[0]! : leaves[1]!;
      return leaves[0]?.side === which ? leaves[0]! : null;
    };
    // Forward, the right page turns over onto the left; back, the reverse.
    const turning = side(from, direction > 0 ? 'right' : 'left');
    const landing = side(to, direction > 0 ? 'left' : 'right');
    const stays = side(from, direction > 0 ? 'left' : 'right');
    const revealed = side(to, direction > 0 ? 'right' : 'left');
    if (!turning) return;
    if (stays) overlay.append(place(pageFace(stays), stays));
    if (revealed) overlay.append(place(pageFace(revealed), revealed));
    place(leaf, turning);
    leaf.style.transformOrigin = direction > 0 ? 'left center' : 'right center';
    leaf.append(pageFace(turning), pageFace(landing, 'pdf-flip-face pdf-flip-back'));
    const end = direction > 0 ? -180 : 180;
    keyframes = [{ transform: 'rotateY(0deg)' }, { transform: `rotateY(${end}deg)` }];
  } else {
    const [leaving] = from;
    const [arriving] = to;
    if (!leaving || !arriving) return;
    leaf.style.transformOrigin = 'left center';
    leaf.append(pageFace(null), pageFace(null, 'pdf-flip-face pdf-flip-back'));
    if (direction > 0) {
      overlay.append(place(pageFace(arriving), arriving));
      place(leaf, leaving);
      leaf.firstElementChild!.replaceWith(pageFace(leaving));
      keyframes = [
        { transform: 'rotateY(0deg)', opacity: 1 },
        { transform: 'rotateY(-90deg)', opacity: 1, offset: 0.5 },
        { transform: 'rotateY(-180deg)', opacity: 0 },
      ];
    } else {
      overlay.append(place(pageFace(leaving), leaving));
      place(leaf, arriving);
      leaf.firstElementChild!.replaceWith(pageFace(arriving));
      keyframes = [
        { transform: 'rotateY(-180deg)', opacity: 0 },
        { transform: 'rotateY(-90deg)', opacity: 1, offset: 0.5 },
        { transform: 'rotateY(0deg)', opacity: 1 },
      ];
    }
  }

  overlay.append(leaf);
  v.container.append(overlay);
  v.container.classList.add('is-flipping');
  const animation = leaf.animate(keyframes, { duration: TURN_MS, easing: 'ease-in-out' });
  const current = { animation, overlay, container: v.container };
  flip = current;
  animation.onfinish = () => {
    if (flip === current) finishFlip();
  };
}

/** End a turn that is still animating, straight to its last frame. */
function finishFlip(): void {
  if (!flip) return;
  const { animation, overlay, container } = flip;
  flip = null;
  animation.cancel();
  // Zeroed so the stills' pixel buffers go now, as releasePage does.
  for (const canvas of overlay.querySelectorAll('canvas')) {
    canvas.width = 0;
    canvas.height = 0;
  }
  overlay.remove();
  container.classList.remove('is-flipping');
}

/** Horizontal swipes turn pages on a touch screen; vertical ones still scroll. */
function listenForSwipes(v: Viewer): void {
  let start: { x: number; y: number } | null = null;
  const { signal } = v.listeners;
  v.container.addEventListener('pointerdown', (event) => {
    start = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null;
  }, { signal });
  v.container.addEventListener('pointercancel', () => { start = null; }, { signal });
  v.container.addEventListener('pointerup', (event) => {
    if (!start || !isPaged(v)) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    start = null;
    if (Math.abs(dx) > 60 && Math.abs(dy) < Math.abs(dx) / 2) turnPdfPage(dx < 0 ? 1 : -1);
  }, { signal });
}

// ── Outline ───────────────────────────────────────────────────────────────────

interface RawOutlineItem {
  title: string;
  dest: string | unknown[] | null;
  items?: RawOutlineItem[];
}

async function resolveOutline(doc: PdfDocument): Promise<PdfOutlineItem[]> {
  const raw = (await doc.getOutline()) as RawOutlineItem[] | null;
  if (!raw?.length) return [];

  const pageOf = async (dest: RawOutlineItem['dest']): Promise<number | null> => {
    try {
      const explicit = typeof dest === 'string' ? await doc.getDestination(dest) : dest;
      const target = Array.isArray(explicit) ? explicit[0] : null;
      if (typeof target === 'number') return target + 1; // some destinations hold a page index directly
      if (target && typeof target === 'object') return (await doc.getPageIndex(target as never)) + 1;
    } catch {
      // A broken destination just doesn't get an entry.
    }
    return null;
  };

  const out: PdfOutlineItem[] = [];
  const walk = async (items: RawOutlineItem[], depth: number): Promise<void> => {
    for (const item of items) {
      const page = item.dest ? await pageOf(item.dest) : null;
      const title = item.title?.replace(/\s+/g, ' ').trim();
      if (page && title) out.push({ title, page, depth });
      if (item.items?.length) await walk(item.items, depth + 1);
    }
  };
  await walk(raw, 0);
  return out;
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

/** Stop observing pages and drop the loaded document — call before leaving the view. */
export function unloadPdf(): void {
  const v = viewer;
  viewer = null;
  if (!v) return;

  finishFlip();
  v.listeners.abort();
  v.renderObserver.disconnect();
  v.releaseObserver.disconnect();
  v.resizeObserver.disconnect();
  window.removeEventListener('scroll', v.scrollHandler);
  for (const state of v.pages) releasePage(state);
  v.loadingTask.destroy();
  v.container.replaceChildren();
}

interface PageSize {
  width: number;
  height: number;
}

/** Give pages their real size, holding the reader's place (keepReadingPoint)
 *  so a correction to pages above the one being read doesn't move it. */
function resizePages(v: Viewer, sizes: Array<[PageState, PageSize]>): void {
  const changed = sizes.filter(([s, { width, height }]) => s.widthUnits !== width || s.heightUnits !== height);
  if (!changed.length) return;

  // The scroll handler only catches up on the next frame; the anchor has to be
  // the page that's under the reading line now.
  if (!isPaged(v)) updateCurrentPage(v);
  keepReadingPoint(v, () => {
    for (const [state, { width, height }] of changed) {
      state.widthUnits = width;
      state.heightUnits = height;
      state.wrapper.style.aspectRatio = `${width} / ${height}`;
      state.wrapper.style.setProperty('--pdf-page-aspect', String(width / height));
    }
  }, changed.map(([state]) => state));
}

/**
 * Pages are laid out with page 1's size to begin with, so the viewer can show
 * up as soon as the document opens, rather than after every page has been read
 * (which, loading by range, would mean fetching most of the file first). The
 * real sizes are filled in here; most PDFs are uniform, so nothing moves.
 */
async function readPageSizes(v: Viewer): Promise<void> {
  for (let start = 1; start <= v.pages.length; start += SIZE_BATCH) {
    const batch = v.pages.slice(start - 1, start - 1 + SIZE_BATCH);
    const sizes = await Promise.all(
      batch.map(async (state): Promise<[PageState, PageSize]> => {
        const page = await v.doc.getPage(state.number);
        return [state, page.getViewport({ scale: 1 })];
      }),
    );
    if (v !== viewer) return;
    resizePages(v, sizes);
  }
  const aspect = medianAspect(v.pages);
  if (aspect !== v.pages[0]!.widthUnits / v.pages[0]!.heightUnits) v.hooks.onPageAspect?.(aspect);
}

function medianAspect(pages: PageState[]): number {
  const aspects = pages.map((p) => p.widthUnits / p.heightUnits).sort((a, b) => a - b);
  return aspects[Math.floor(aspects.length / 2)] ?? 1 / Math.SQRT2;
}

/**
 * Fetch a bookmark's PDF and lay it out in `container`, one placeholder per
 * page, drawn lazily as pages near the viewport. Throws on failure — the
 * caller renders its own error UI, matching how the HTML article path works.
 */
export async function openPdf(
  container: HTMLElement,
  bookmarkId: number,
  hooks: PdfHooks = {},
): Promise<PdfInfo> {
  unloadPdf();
  container.replaceChildren();

  const lib = await loadPdfjs();
  // Given the URL rather than the downloaded bytes, pdf.js fetches by range:
  // the pages being looked at load first, and the rest of the file streams in
  // behind them, instead of the whole file arriving before anything shows.
  const loadingTask = lib.getDocument({
    url: `${api.baseUrl}/bookmarks/${bookmarkId}/pdf`,
    httpHeaders: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
  });
  let doc: PdfDocument;
  try {
    doc = await loadingTask.promise;
  } catch (err) {
    if (err instanceof lib.ResponseException) throw new Error(`Could not load the PDF (HTTP ${err.status})`);
    throw err;
  }
  const first = (await doc.getPage(1)).getViewport({ scale: 1 });

  container.style.setProperty('--pdf-zoom', String(readZoom()));
  container.toggleAttribute('data-zoomed', readZoom() > 1);
  container.dataset.colors = colors;
  applyLayout(container);

  const { width, height } = first;
  const pages: PageState[] = Array.from({ length: doc.numPages }, (_, i) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-page';
    wrapper.dataset.pageNumber = String(i + 1);
    // Reserve the page's aspect-ratio-correct height up front (page 1's until
    // readPageSizes has its own) so lazily rendering it in doesn't jump the
    // scroll position of pages below it.
    wrapper.style.aspectRatio = `${width} / ${height}`;
    // The paged view sizes each page to fit the window's height.
    wrapper.style.setProperty('--pdf-page-aspect', String(width / height));
    container.appendChild(wrapper);
    return {
      number: i + 1, wrapper, widthUnits: width, heightUnits: height,
      canvas: null, targetScale: 0, task: null, generation: 0, hasTextLayer: false,
    };
  });
  const byWrapper = new Map(pages.map((p) => [p.wrapper, p]));

  const renderObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && viewer) renderPage(viewer, byWrapper.get(entry.target as HTMLElement)!);
    }
  }, { rootMargin: RENDER_MARGIN });

  const releaseObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) releasePage(byWrapper.get(entry.target as HTMLElement)!);
    }
  }, { rootMargin: RELEASE_MARGIN });

  const resizeObserver = new ResizeObserver((entries) => {
    if (!viewer) return;
    for (const entry of entries) {
      if (entry.target === container) updateSpread(viewer);
      else refreshPage(viewer, byWrapper.get(entry.target as HTMLElement)!);
    }
  });

  let frame = 0;
  const scrollHandler = (): void => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      // Paged, the current page is whichever was turned to, not scrolled to.
      if (viewer && !isPaged(viewer)) updateCurrentPage(viewer);
    });
  };
  window.addEventListener('scroll', scrollHandler, { passive: true });

  viewer = {
    lib, doc, loadingTask, container, pages, hooks,
    renderObserver, releaseObserver, resizeObserver, scrollHandler,
    listeners: new AbortController(), currentPage: 1,
  };

  container.toggleAttribute('data-spread', wantsSpread(container));
  container.toggleAttribute('data-paged', reading === 'paged');
  if (reading === 'paged') showSpread(viewer, 1);
  listenForSwipes(viewer);
  resizeObserver.observe(container);
  for (const state of pages) {
    applyScaleFactor(state);
    renderObserver.observe(state.wrapper);
    releaseObserver.observe(state.wrapper);
    resizeObserver.observe(state.wrapper);
  }
  void readPageSizes(viewer).catch((err) => console.error('Reading PDF page sizes failed', err));

  return {
    pageCount: doc.numPages,
    pageAspect: width / height,
    loadOutline: () => resolveOutline(doc),
  };
}
